import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import BrandLogo from './components/BrandLogo';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getCertificateSlug } from './data/websiteContent';
import AboutPage from './pages/AboutPage';
import CertificateDetailPage from './pages/CertificateDetailPage';
import CertificateCreatePage from './pages/CertificateCreatePage';
import CertificateVerificationPage from './pages/CertificateVerificationPage';
import ContactPage from './pages/ContactPage';
import MarketingHomePage from './pages/MarketingHomePage';
import OfferingCertificatesPage from './pages/OfferingCertificatesPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import AdminInquiriesPage from './pages/AdminInquiriesPage';
import './styles/App.css';

const footerServices = ['ISO 9001:2015', 'GMP Compliance', 'ISO 13485:2016', 'CE Marking', 'Organic Compliance'];

const AppContent = () => {
  const { isAuthenticated, username, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');

  const publicSections = useMemo(
    () => [
      { id: 'home', label: 'Home' },
      { id: 'about', label: 'About' },
      { id: 'services', label: 'Services' },
      { id: 'contact', label: 'Contact' },
    ],
    []
  );

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'nav-link active' : 'nav-link';

  const sectionLinkClass = (sectionId: string) =>
    activeSection === sectionId ? 'nav-link active' : 'nav-link';

  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visibleEntry?.target instanceof HTMLElement) {
          setActiveSection(visibleEntry.target.id);
        }
      },
      {
        root: null,
        threshold: [0.18, 0.35, 0.55],
        rootMargin: '-12% 0px -58% 0px',
      }
    );

    publicSections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    const currentHash = location.hash.replace('#', '');
    if (currentHash) {
      setActiveSection(currentHash);
    }

    return () => observer.disconnect();
  }, [location.hash, location.pathname, publicSections]);

  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    const sectionId = location.hash.replace('#', '') || 'home';
    const element = document.getElementById(sectionId);

    if (element) {
      window.requestAnimationFrame(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      setActiveSection(sectionId);
    }
  }, [location.hash, location.pathname]);

  const navigateToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate({ pathname: '/', hash: `#${sectionId}` });
      return;
    }

    navigate({ pathname: '/', hash: `#${sectionId}` });
  };

  return (
    <div className="app">
      <nav className="navbar">
        <div className="nav-container">
          <BrandLogo />

          <ul className="nav-menu" aria-label="Primary navigation">
            {publicSections.map((section) => (
              <li className="nav-item" key={section.id}>
                <button
                  type="button"
                  className={sectionLinkClass(section.id)}
                  onClick={() => navigateToSection(section.id)}
                >
                  {section.label}
                </button>
              </li>
            ))}
          </ul>

          <div className="nav-session">
            {isAuthenticated ? (
              <>
                <span className="nav-user" title={username || 'Signed in'}>
                  {username}
                </span>
                <NavLink to="/create" className={navLinkClass}>
                  Create
                </NavLink>
                <NavLink to="/verify" className={navLinkClass}>
                  Verify
                </NavLink>
                <NavLink to="/inquiries" className={navLinkClass}>
                  Inquiries
                </NavLink>
                <button type="button" className="nav-link nav-button" onClick={logout}>
                  Logout
                </button>
              </>
            ) : (
              <>
                <NavLink to="/verify" className={navLinkClass}>
                  Verify
                </NavLink>
              <NavLink to="/login" className={navLinkClass}>
                Login
              </NavLink>
              </>
            )}
          </div>
        </div>
      </nav>

      <main className="main-content">
        <Routes>
          <Route path="/" element={<MarketingHomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/offering-certificates" element={<OfferingCertificatesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/dashboard" element={<HomePage />} />
          <Route path="/:slug.html" element={<CertificateDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <CertificateCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inquiries"
            element={
              <ProtectedRoute>
                <AdminInquiriesPage />
              </ProtectedRoute>
            }
          />
          <Route path="/verify/:certificateNumber" element={<CertificateVerificationPage />} />
          <Route path="/verify" element={<CertificateVerificationPage />} />
        </Routes>
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="footer-brand">
            <h2>
              <span>Certification</span>ISO
            </h2>
            <p>
              CertificationISO supports organizations with certification planning, document review,
              and audit-readiness guidance for ISO, GMP, CE, organic, and other compliance
              programs.
            </p>
          </div>

          <div className="footer-column">
            <h3>Useful Links</h3>
            <ul>
              <li>
                <NavLink to="/#about">About Us</NavLink>
              </li>
              <li>
                <NavLink to="/#services">Services</NavLink>
              </li>
              <li>
                <NavLink to="/#contact">Contact</NavLink>
              </li>
              <li>
                <NavLink to="/verify">Verify Certificate</NavLink>
              </li>
            </ul>
          </div>

          <div className="footer-column">
            <h3>Our Services</h3>
            <ul>
              {footerServices.map((service) => (
                <li key={service}>
                  <NavLink to={`/${getCertificateSlug(service)}.html`}>{service}</NavLink>
                </li>
              ))}
              <li>
                <NavLink to="/#services">Read More</NavLink>
              </li>
            </ul>
          </div>

          <div className="footer-column footer-contact">
            <h3>Contact Info</h3>
            <ul>
              <li>
                <span aria-hidden="true">MAIL</span>
                <a href="mailto:shivam@landsky.in">info@certificationiso.com</a>
              </li>
              <li>
                <span aria-hidden="true">MAIL</span>
                <a href="mailto:pooja@landsky.in">info@certificationiso.com</a>
              </li>
              <li>
                <span aria-hidden="true">PIN</span>
                <address>
                  5/346, Sector-5, Jankipuram Ext. Lucknow - 226031
                </address>
              </li>
            </ul>
            <h3 className="footer-social-title">Our Social Networks</h3>
            <div className="footer-socials" aria-label="Social links">
              <a href="https://twitter.com" aria-label="Twitter">X</a>
              <a href="https://facebook.com" aria-label="Facebook">f</a>
              <a href="https://instagram.com" aria-label="Instagram">IG</a>
            </div>
          </div>
        </div>

        <div className="footer-copyright">
          <p>Copyright CertificationISO All Rights Reserved</p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
